import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
} from 'react'
import {
  TextInput,
  ImageInput,
  ImageField,
  SimpleForm,
  required,
  useDataProvider,
  useNotify,
  SaveContextProvider,
  useGetIdentity,
  Toolbar,
  SaveButton,
  useRedirect,
  Title,
} from 'react-admin'

const ProfileContext = createContext()

export const ProfileProvider = ({ children }) => {
  const [profileVersion, setProfileVersion] = useState(0)

  const context = useMemo(
    () => ({
      profileVersion,
      refreshProfile: () => setProfileVersion((currentVersion) => currentVersion + 1),
    }),
    [profileVersion]
  )

  return <ProfileContext.Provider value={context}>{children}</ProfileContext.Provider>
}

export const useProfile = () => useContext(ProfileContext)

const CustomToolbar = ({ ...props }) => {
  return (
    <Toolbar {...props}>
      <SaveButton />
    </Toolbar>
  )
}

export const ProfileEdit = ({ staticContext, ...props }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState(null)
  const { refreshProfile } = useProfile()
  const redirect = useRedirect()
  const { isLoading, data: identity } = useGetIdentity()

  // Base handleSave for use with BE, after updating dataProvider.js. Need to test!
  // const handleSave = useCallback(
  //   (values) => {
  //     console.log('handleSave - values', values)
  //     setSaving(true)
  //     dataProvider.updateUserProfile(
  //       { data: values },
  //       {
  //         onSuccess: ({ data }) => {
  //           console.log('onSuccess', data)

  //           setSaving(false)
  //           notify('Your profile has been updated', 'info', {
  //             '*': 'Your profile has been updated',
  //           })
  //           refreshProfile()
  //         },
  //         onFailure: () => {
  //           setSaving(false)
  //           notify(
  //             'A technical error occurred while updating your profile. Please try later.',
  //             'warning',
  //             {
  //               '*': 'A technical error occurred while updating your profile. Please try later.',
  //             }
  //           )
  //         },
  //       }
  //     )
  //   },
  //   [dataProvider, notify, refreshProfile]
  // )

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await dataProvider.getUserProfile()
        setProfile(data)
      } catch (error) {
        console.error('Error fetching user profile:', error)
        notify('Error loading profile. Please try again.', 'error')
      }
    }

    if (!isLoading && !identity) {
      console.log('Identity is undefined, fetching profile directly')
      fetchProfile()
    } else if (identity) {
      setProfile(identity)
    }
  }, [isLoading, identity, dataProvider, notify])

  const handleSave = useCallback(
    async (values) => {
      setSaving(true)
      try {
        await dataProvider.updateUserProfile({ data: values })
        setSaving(false)
        notify('Your profile has been updated', 'info', {
          '*': 'Your profile has been updated',
        })
        refreshProfile()
        redirect('/')
      } catch (error) {
        console.error('Error updating profile:', error)
        setSaving(false)
        notify(
          'A technical error occurred while updating your profile. Please try later.',
          'warning',
          {
            '*': 'A technical error occurred while updating your profile. Please try later.',
          }
        )
      }
    },
    [dataProvider, notify, refreshProfile]
  )

  const saveContext = useMemo(
    () => ({
      save: handleSave,
      saving,
    }),
    [saving, handleSave]
  )

  if (isLoading || (!identity && !profile)) {
    return <div>Loading...</div>
  }

  return (
    <SaveContextProvider value={saveContext}>
      <Title title="User Settings" />
      <SimpleForm
        save={handleSave}
        record={profile || identity || {}}
        toolbar={<CustomToolbar />} // Hide delete BTN
      >
        <TextInput source="fullName" validate={required()} />
        <ImageInput source="avatar" validate={required()}>
          <ImageField source="src" title="title" />
        </ImageInput>
      </SimpleForm>
    </SaveContextProvider>
  )
}
